import React, { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Camera, Trash2, Loader2, Wand2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

export default function LogoUpload({ entityType, entityId, logoPath, token, onUpdate, canReprocess = false, size = 'md' }) {
  const [uploading, setUploading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const fileRef = useRef(null);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const sizeClass = size === 'sm' ? 'h-12 w-12' : size === 'lg' ? 'h-24 w-24' : 'h-16 w-16';
  const logoUrl = logoPath ? `${BACKEND}/api/logos/${entityType}/${entityId}?t=${bust}` : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Images only'); return; }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const r = await axios.post(`${BACKEND}/api/admin/logos/${entityType}/${entityId}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success(r.data?.processed ? 'Logo uploaded — white bg auto-stripped' : 'Logo uploaded');
      setBust(Date.now());
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove logo?')) return;
    try {
      await axios.delete(`${BACKEND}/api/admin/logos/${entityType}/${entityId}`, { headers });
      toast.success('Logo removed');
      if (onUpdate) onUpdate();
    } catch { toast.error('Failed'); }
  };

  const handleReprocess = async () => {
    setReprocessing(true);
    try {
      const r = await axios.post(`${BACKEND}/api/admin/logos/${entityType}/${entityId}/reprocess`, {}, { headers });
      if (r.data?.status === 'skipped') {
        toast.info(`Skipped — ${r.data.reason || 'already clean'}`);
      } else {
        toast.success('White background stripped');
        setBust(Date.now());
        if (onUpdate) onUpdate();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reprocess failed');
    } finally {
      setReprocessing(false);
    }
  };

  return (
    <div className="flex items-center gap-3" data-testid={`logo-upload-${entityType}`}>
      <div className={`${sizeClass} rounded-lg border-2 border-dashed border-muted-foreground/20 flex items-center justify-center overflow-hidden bg-muted/30 shrink-0 relative group`}>
        {uploading || reprocessing ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : logoUrl ? (
          <>
            <img src={logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
              <button onClick={() => fileRef.current?.click()} className="p-1 rounded bg-white/20 hover:bg-white/40" title="Replace"><Camera className="h-3 w-3 text-white" /></button>
              {canReprocess && (
                <button onClick={handleReprocess} className="p-1 rounded bg-white/20 hover:bg-white/40" title="Reprocess transparency" data-testid={`logo-reprocess-${entityType}`}><Wand2 className="h-3 w-3 text-white" /></button>
              )}
              <button onClick={handleRemove} className="p-1 rounded bg-white/20 hover:bg-white/40" title="Remove"><Trash2 className="h-3 w-3 text-white" /></button>
            </div>
          </>
        ) : (
          <button onClick={() => fileRef.current?.click()} className="w-full h-full flex flex-col items-center justify-center text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors">
            <Camera className="h-4 w-4" />
            <span className="text-[8px] mt-0.5">Logo</span>
          </button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
      {logoUrl && !uploading && !reprocessing && canReprocess && (
        <Button variant="ghost" size="sm" className="text-[10px] text-muted-foreground gap-1 h-7" onClick={handleReprocess} data-testid={`logo-reprocess-btn-${entityType}`}>
          <Wand2 className="h-3 w-3" /> Reprocess transparency
        </Button>
      )}
      {!logoUrl && !uploading && (
        <Button variant="ghost" size="sm" className="text-xs text-muted-foreground" onClick={() => fileRef.current?.click()}>
          Upload Logo
        </Button>
      )}
    </div>
  );
}
